import { useQueryClient } from '@tanstack/react-query'
import BaseTableCard from 'common/components/BaseTableCard'
import BasicTable from 'common/components/BasicTable'
import { Column } from 'common/components/BasicTable/types'
import { DateRange } from 'common/components/DateRangePicker'
import { getCostTransactionsHistoryInfiniteKey } from 'common/constants/queryKeys'
import { useDefaultValueLabel } from 'common/utils/defaultValueLabel'
import { formatThaiShortDateTwoDigitYear, formatTime } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import { useCostTransactionHistory } from 'modules/cost/api/selector'
import { TransactionType } from 'modules/cost/api/types'
import { dashboardDateDisplayValue } from 'modules/food/utils'
import { useGetCycles } from 'modules/pond/api/queries'
import { useSpeciesName } from 'modules/pond/api/selector'
import { selectedCycleIdState } from 'modules/pond/stores/atom'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useRecoilState, useRecoilValue } from 'recoil'

import { Box, Tooltip } from '@mui/material'

import { selectedCostTransactionHistoryDateState } from '../../stores/atom'
import TransactionTypeChip from '../TransactionTypeChip'

const CostTable: React.FC = () => {
  const { t } = useTranslation('cost')
  const label = useDefaultValueLabel()
  const qc = useQueryClient()

  const { data: cycles } = useGetCycles()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const speciesName = useSpeciesName()
  const selectedCycle = cycles?.find((cycle) => cycle.id === cycleId)
  const [dateRange, setDateRange] = useRecoilState(selectedCostTransactionHistoryDateState)
  const {
    data: costTransactionsData,
    isLoading: isLoadingCostTransactions,
    hasNextPage,
    fetchNextPage,
  } = useCostTransactionHistory()

  const columns = [
    {
      id: 'date',
      title: t('columns.date'),
      width: '150px',
      format: (val: string) => (
        <Box sx={{ whiteSpace: 'pre-line' }}>
          {formatThaiShortDateTwoDigitYear(val) + '\n' + formatTime(val)}
        </Box>
      ),
    },
    {
      id: 'shrimpAge',
      title: <Trans i18nKey={'pond:animal.age'} values={{ species: speciesName }} />,
      width: '100px',
    },
    { id: 'category', title: t('columns.category'), width: '80px' },
    {
      id: 'type',
      title: t('columns.type'),
      width: '80px',
      format: (val: string) => <TransactionTypeChip transactionType={val as TransactionType} />,
    },
    { id: 'name', title: t('columns.name'), width: '150px' },
    { id: 'lot', title: t('columns.lot'), width: '100px' },
    {
      id: 'quantity',
      title: t('columns.quantity'),
      width: '150px',
      format: (v: string) => {
        const vals = v.split(' ')
        if (vals.length === 2) {
          return fNumber(Number(vals[0])) + ' ' + vals[1]
        }
        return v
      },
    },
    {
      id: 'pricePerUnit',
      title: t('columns.pricePerUnit'),
      width: '150px',
      format: (v: number) => fNumber(v),
    },
    {
      id: 'total',
      title: t('columns.total'),
      width: '150px',
      format: (v: number) => fNumber(v),
      align: 'center',
    },
  ] as Column[]
  
  const rows =
    costTransactionsData?.map((r) => {
      const fullName = `${label.costCategory(r.name)}${r.stock_deleted_at ? ` ${t('deleted_suffix')}` : ''}`

      const shrimpAge = selectedCycle
        ? differenceInCalendarDays(new Date(r.date_time), new Date(selectedCycle.startDate)) + 1
        : null

      return {
        date: r.date_time,
        shrimpAge: shrimpAge ?? '-',
        category: r.category?.name ? label.costCategory(r.category.name) : '-',
        type: r.transaction_type,
        name: (
          <Tooltip title={fullName} arrow>
            <span>{fullName}</span>
          </Tooltip>
        ),
        lot: r.lot || '-',
        quantity: typeof r.quantity === 'number' ? `${r.quantity} ${label.unit(r.unit)}` : r.quantity ?? '',
        pricePerUnit: r.price_per_unit ?? 0,
        total: r.total_price ?? 0,
      }
    }) ?? []

  const handleClearDateRange = () => {
    if (cycleId && cycles) {
      const selectedCycle = cycles.find((cycle) => cycle.id === cycleId)
      if (selectedCycle) {
        setDateRange({
          startDate: new Date(selectedCycle.startDate),
          endDate: selectedCycle.endDate ? new Date(selectedCycle.endDate) : new Date(),
        })
      }
    }
  }

  const onLoadMoreRows = () => {
    if (hasNextPage) {
      fetchNextPage()
    }
  }

  return (
    <BaseTableCard
      title={t('summary')}
      refresh={() => {
        qc.invalidateQueries({ queryKey: getCostTransactionsHistoryInfiniteKey.all() })
      }}
      disableRefresh={true}
      isLoading={isLoadingCostTransactions}
      isEmpty={!isLoadingCostTransactions && rows.length === 0}
      isDateRangePicker={true}
      dateRange={dateRange as DateRange}
      onDateRangeChange={(dr: DateRange) => setDateRange(dr)}
      displayValue={dashboardDateDisplayValue}
      onClearDateRange={handleClearDateRange}
    >
      <BasicTable
        columns={columns || []}
        rows={rows || []}
        onLoading={onLoadMoreRows}
        isLoading={isLoadingCostTransactions}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '12px 20px',
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      ></Box>
    </BaseTableCard>
  )
}

export default CostTable
